import { GET, POST } from '@/shared/api/requests';
import { Client, Project, Layout, SignedUrlResponse, ScanFormData, ScanCreateResponse } from '../types';

/**
 * Get list of clients (for staff users)
 */
export const getClients = async (hasActiveProjects: boolean = true): Promise<Client[]> => {
  const response = await GET(`/base/clients/?has_active_projects=${hasActiveProjects}`);
  return response.data;
};

/**
 * Get projects for a specific client
 */
export const getProjectsForClient = async (
  clientId: number,
  activeOnly: boolean = true
): Promise<Project[]> => {
  const response = await GET(`/projects-for-client/?client_id=${clientId}&active_only=${activeOnly}`);
  return response.data;
};

/**
 * Get layouts for a specific project
 */
export const getLayoutsForProject = async (
  projectId: number,
  layoutType?: string
): Promise<Layout[]> => {
  const layoutTypeParam = layoutType ? `&layout_type=${layoutType}` : '';
  const response = await GET(`/layouts-for-project/?project_id=${projectId}${layoutTypeParam}`);
  return response.data;
};

/**
 * Get signed URLs for file uploads to GCS
 */
export const getSignedUrls = async (
  fileNames: string[],
  scanType: string,
  projectId: number,
  scanDate: string,
  layoutName?: string
): Promise<SignedUrlResponse> => {
  const response = await POST('/scans/get-signed-urls/', {
    file_names: fileNames,
    scan_type: scanType,
    project_id: projectId,
    scan_date: scanDate,
    layout_name: layoutName,
    content_type: 'image/jpeg'
  });
  return response.data;
};

/**
 * Create a new scan with uploaded photos
 */
export const createScan = async (
  scanData: ScanFormData,
  fileNames: string[],
  uploadedUrls: string[]
): Promise<ScanCreateResponse> => {
  const response = await POST('/scans/create/', {
    ...scanData,
    file_names: fileNames,
    uploaded_urls: uploadedUrls
  });
  return response.data;
};

/**
 * Upload a file to GCS using a signed URL
 */
export const uploadFileToGCS = async (
  file: File,
  signedUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Upload multiple files to GCS with progress tracking
 */
export const uploadFilesToGCS = async (
  files: File[],
  signedUrls: SignedUrlResponse,
  onProgress?: (overall: number, fileIndex: number, fileProgress: number) => void
): Promise<string[]> => {
  const fileProgresses = new Array(files.length).fill(0);
  const uploadedUrls: string[] = [];

  const updateProgress = () => {
    if (onProgress) {
      const totalProgress = fileProgresses.reduce((sum, p) => sum + p, 0);
      const overallProgress = (totalProgress / files.length);
      onProgress(overallProgress, -1, -1);
    }
  };

  // Upload files with concurrency limit
  const concurrencyLimit = 3;
  for (let i = 0; i < files.length; i += concurrencyLimit) {
    const batch = files.slice(i, i + concurrencyLimit);
    const batchPromises = batch.map((file, batchIndex) => {
      const fileIndex = i + batchIndex;
      const urlData = signedUrls.urls[file.name];
      
      return uploadFileToGCS(file, urlData.signed_url, (progress) => {
        fileProgresses[fileIndex] = progress;
        if (onProgress) {
          onProgress(-1, fileIndex, progress);
        }
        updateProgress();
      }).then(() => urlData.gcs_url);
    });

    const batchResults = await Promise.all(batchPromises);
    uploadedUrls.push(...batchResults);
  }

  return uploadedUrls;
};

